import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eyebrow } from "@/components/SectionHeader";
import { CONTACT } from "@/lib/site";
import {
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  Check,
} from "lucide-react";

const ROLES = [
  "Product",
  "Engineering",
  "Compliance",
  "Operations",
  "Business",
  "Other",
];

const INTENTS = [
  "General inquiry",
  "Book a demo",
  "Partnership",
  "Developer / API access",
];

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    company: "",
    role: ROLES[0],
    email: "",
    intent: INTENTS[0],
    message: "",
  });

  const set = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.company) {
      toast.error("Please complete name, company and email.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        "https://tan-goose-331914.hostingersite.com/wordpress/wp-json/custom/v1/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            full_name: form.name,
            company: form.company,
            work_email: form.email,
            role: form.role,
            talk_about: form.intent,
            tell_us_more: form.message,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setSent(true);
        toast.success(data.message || "Thanks — we'll be in touch shortly.");
      } else {
        toast.error(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to connect to the server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="pt-40 pb-16 md:pb-20 bg-[color:var(--p10-ivory)] border-b border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Eyebrow>Contact</Eyebrow>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6 max-w-5xl"
          >
            Let's connect your business
            <br />
            <em className="text-[color:var(--p10-royal)]">
              to the world.
            </em>
          </motion.h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* FORM */}
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[color:var(--p10-line)] bg-white p-10 md:p-14"
                data-testid="contact-success"
              >
                <span className="w-12 h-12 flex items-center justify-center bg-[color:var(--p10-royal)] text-white">
                  <Check className="w-6 h-6" />
                </span>

                <h2 className="font-serif-display text-4xl md:text-5xl mt-8 leading-tight">
                  Message received.
                </h2>

                <p className="mt-4 text-lg text-[color:var(--p10-ink)]/70 leading-relaxed max-w-lg">
                  A member of our team will reach out shortly — usually within
                  one business day. In the meantime, feel free to explore the
                  site.
                </p>

                <button
                  onClick={() => setSent(false)}
                  className="btn-square btn-secondary mt-10"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="space-y-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow block mb-3">
                      Your name
                    </label>

                    <input
                      data-testid="contact-input-name"
                      className="p10-input"
                      placeholder="Jane Tan"
                      value={form.name}
                      onChange={set("name")}
                    />
                  </div>

                  <div>
                    <label className="eyebrow block mb-3">
                      Company
                    </label>

                    <input
                      data-testid="contact-input-company"
                      className="p10-input"
                      placeholder="Neobank Ltd."
                      value={form.company}
                      onChange={set("company")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow block mb-3">
                      Work email
                    </label>

                    <input
                      data-testid="contact-input-email"
                      type="email"
                      className="p10-input"
                      placeholder="jane@company.com"
                      value={form.email}
                      onChange={set("email")}
                    />
                  </div>

                  <div>
                    <label className="eyebrow block mb-3">
                      Role
                    </label>

                    <select
                      data-testid="contact-input-role"
                      className="p10-input"
                      value={form.role}
                      onChange={set("role")}
                    >
                      {ROLES.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="eyebrow block mb-3">
                    I'd like to talk about
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {INTENTS.map((i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() =>
                          setForm({
                            ...form,
                            intent: i,
                          })
                        }
                        data-testid={`contact-intent-${i
                          .replace(/\s|\/|-/g, "")
                          .toLowerCase()}`}
                        className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors font-mono-tech ${
                          form.intent === i
                            ? "bg-[color:var(--p10-ink)] text-white border-[color:var(--p10-ink)]"
                            : "border-[color:var(--p10-line)] text-[color:var(--p10-ink)]/70 hover:border-[color:var(--p10-ink)]"
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="eyebrow block mb-3">
                    Tell us a little more
                  </label>

                  <textarea
                    data-testid="contact-input-message"
                    className="p10-input resize-none"
                    rows={5}
                    placeholder="Corridors, currencies, timelines, or anything else that helps us prepare."
                    value={form.message}
                    onChange={set("message")}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-4">
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={loading}
                    className="btn-square btn-primary group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : "Send message"}
                    <ArrowUpRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>

                  <p className="text-xs text-[color:var(--p10-ink)]/50 max-w-sm leading-relaxed">
                    By submitting, you agree to be contacted about Pay10x
                    products and services.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* SIDE */}
          <aside className="lg:col-span-5 lg:pl-12 lg:border-l lg:border-[color:var(--p10-line)]">
            <Eyebrow>Direct channels</Eyebrow>

            <div className="mt-8 space-y-8">
              <div>
                <div className="flex items-center gap-3 text-[color:var(--p10-ink)]/50 mb-2">
                  <Mail className="w-4 h-4" />
                  <span className="font-mono-tech text-xs tracking-[0.2em] uppercase">
                    Email
                  </span>
                </div>

                <a
                  data-testid="contact-email"
                  href={`mailto:${CONTACT.email}`}
                  className="font-serif-display text-3xl md:text-4xl link-underline"
                >
                  {CONTACT.email}
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 text-[color:var(--p10-ink)]/50 mb-2">
                  <Phone className="w-4 h-4" />
                  <span className="font-mono-tech text-xs tracking-[0.2em] uppercase">
                    Phone
                  </span>
                </div>

                <a
                  data-testid="contact-phone"
                  href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                  className="font-serif-display text-3xl md:text-4xl link-underline"
                >
                  {CONTACT.phone}
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 text-[color:var(--p10-ink)]/50 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span className="font-mono-tech text-xs tracking-[0.2em] uppercase">
                    Headquarters
                  </span>
                </div>

                <div className="font-serif-display text-3xl md:text-4xl">
                  Singapore
                </div>

                <div className="mt-2 text-sm text-[color:var(--p10-ink)]/60">
                  {CONTACT.company}
                </div>
              </div>
            </div>

            <div className="mt-14 p-8 bg-[color:var(--p10-warm)]/40 border border-[color:var(--p10-line)]">
              <div className="eyebrow mb-3">
                Prefer async?
              </div>

              <p className="font-serif-display text-2xl leading-tight">
                Send us a short brief on your corridors and use case — we'll
                come back with a proposed integration plan.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}